import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

// Searchable <select>. With a `url` value the options are fetched from the
// server as the user types, so lists too long to render inline (clients) stay
// usable and the page stays small.
//
//   <%= f.collection_select :client_id, [], :id, :name, {},
//         data: { controller: "tom-select",
//                 "tom-select-url-value": search_path } %>
export default class extends Controller {
  static values = {
    url: String,
    placeholder: { type: String, default: "Buscar…" },
    minChars: { type: Number, default: 2 }
  }

  connect() {
    const remote = this.hasUrlValue && this.urlValue.length > 0

    this.select = new TomSelect(this.element, {
      valueField: "id",
      labelField: "text",
      searchField: remote ? [] : ["text"],
      placeholder: this.placeholderValue,
      maxOptions: 50,
      // A blank option in the markup is the "no selection" choice; keep it
      // clearable rather than forcing a pick.
      allowEmptyOption: true,
      // Server-side search already ranks and filters; re-scoring locally would
      // hide results the server deliberately returned.
      score: remote ? () => () => 1 : undefined,
      load: remote ? this.load.bind(this) : undefined,
      shouldLoad: remote ? (q) => q.length >= this.minCharsValue : undefined,
      render: {
        no_results: (data, escape) =>
          `<div class="no-results">Sin resultados para "${escape(data.input)}"</div>`,
        loading: () => '<div class="loading-more-results">Buscando…</div>'
      }
    })

    // On an edit form the saved record must stay visible even though the
    // remote list has not been fetched yet.
    if (remote) this.preserveSelected()
  }

  disconnect() {
    if (this.select) {
      this.select.destroy()
      this.select = null
    }
  }

  load(query, callback) {
    const url = `${this.urlValue}${this.urlValue.includes("?") ? "&" : "?"}q=${encodeURIComponent(query)}`
    fetch(url, { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : []))
      .then((items) => callback(items))
      .catch(() => callback())
  }

  preserveSelected() {
    const selected = this.element.querySelector("option[selected]") ||
      Array.from(this.element.options).find((o) => o.value && o.defaultSelected)
    if (!selected) return

    this.select.addOption({ id: selected.value, text: selected.textContent.trim() })
    this.select.addItem(selected.value, true)
  }
}
